import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Calculator, DollarSign, TrendingUp, PieChart, Loader2, Save } from "lucide-react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id } from "../../../../../convex/_generated/dataModel";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/utils";

interface ProfitAnalyzerToolProps {
  bidId?: Id<"bids">;
  onClose?: () => void;
  onSave?: (data: any) => void;
}

export function ProfitAnalyzerTool({ bidId, onClose, onSave }: ProfitAnalyzerToolProps) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(bidId ? true : false);

  // Only tax rate is editable - revenue and costs are derived from source data
  const [taxRate, setTaxRate] = useState<number>(0);

  // Track if the component has been initialized
  const [initialized, setInitialized] = useState<boolean>(false);

  // Track if we're currently saving
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Fetch existing profit analysis from Convex (only for tax rate)
  const existingProfitAnalysis = useQuery(
    api.bidProfitAnalysis.getBidProfitAnalysis,
    bidId ? { bidId } : "skip"
  );

  // Fetch bid summary for recommended price (revenue source)
  const bidSummary = useQuery(api.bidPricing.getBidSummary, bidId ? { bidId } : "skip");

  // Fetch bid cost summary
  const costSummary = useQuery(api.bidCostSummaries.getBidCostSummary, bidId ? { bidId } : "skip");

  // Save profit analysis mutation
  const saveProfitAnalysisMutation = useMutation(api.bidProfitAnalysis.saveBidProfitAnalysis);

  // Recalculate cost summary mutation
  const recalculateCostSummaryMutation = useMutation(
    api.bidCostSummaries.recalculateBidCostSummary
  );

  // Track if recalculation has been performed
  const [recalculationPerformed, setRecalculationPerformed] = useState(false);

  // Recalculate cost summary when component loads
  useEffect(() => {
    if (bidId && !recalculationPerformed) {
      const recalculateCosts = async () => {
        try {
          console.log("Recalculating cost summary for bid:", bidId);
          const result = await recalculateCostSummaryMutation({ bidId });
          console.log("Cost summary recalculated successfully, result:", result);
          setRecalculationPerformed(true);
        } catch (error) {
          console.error("Error recalculating cost summary:", error);
          // Still mark as performed to avoid infinite retries
          setRecalculationPerformed(true);
        }
      };

      recalculateCosts();
    }
  }, [bidId, recalculationPerformed, recalculateCostSummaryMutation]);

  // Load tax rate when profit analysis query returns
  useEffect(() => {
    if (bidId) {
      // Check if all queries have completed (even if they returned null)
      const queriesCompleted =
        existingProfitAnalysis !== undefined &&
        bidSummary !== undefined &&
        costSummary !== undefined;

      if (!queriesCompleted) {
        return; // Exit early and wait for all queries to complete
      }

      // Wait for recalculation to complete before loading data
      if (!recalculationPerformed) {
        return; // Exit early and wait for recalculation to complete
      }

      // Load tax rate from existing profit analysis if available
      if (existingProfitAnalysis && typeof existingProfitAnalysis.taxRate === "number") {
        setTaxRate(existingProfitAnalysis.taxRate);
      }

      // Set loading state to false
      setIsLoading(false);
      setInitialized(true);
    } else {
      // No bidId, so no loading needed
      setIsLoading(false);
      setInitialized(true);
    }
  }, [existingProfitAnalysis, bidSummary, costSummary, bidId, recalculationPerformed]);

  // Derive revenue and costs from source data (read-only, always in sync)
  const revenue = bidSummary?.recommendedMonthlyPrice ?? 0;
  const laborCost = costSummary?.totalLaborCost ?? 0;
  const materialCost = costSummary?.totalSupplyCost ?? 0;
  const overheadCost = costSummary?.totalOperationalCost ?? 0;

  // Calculate profit metrics
  const safeTaxRate = typeof taxRate === "number" ? taxRate : 0;
  const totalCost = laborCost + materialCost + overheadCost;
  const grossProfit = revenue - totalCost;
  const grossProfitMargin = revenue > 0 ? (grossProfit / revenue) * 100 : 0;
  const taxAmount = (grossProfit * safeTaxRate) / 100;
  const netProfit = grossProfit - taxAmount;
  const netProfitMargin = revenue > 0 ? (netProfit / revenue) * 100 : 0;
  const breakEvenRevenue = safeTaxRate < 100 ? totalCost / (1 - safeTaxRate / 100) : totalCost;

  const handleSave = async () => {
    if (!bidId || isSaving) return;

    // Create the profit analysis data object
    // Revenue and costs are derived from source data, only tax rate is user input
    const profitAnalysisData = {
      revenue,
      laborCost,
      materialCost,
      overheadCost,
      taxRate: safeTaxRate,
      totalCost,
      grossProfit,
      grossProfitMargin,
      taxAmount,
      netProfit,
      netProfitMargin,
      breakEvenRevenue,
    };

    setIsSaving(true);
    try {
      // Save to Convex
      await saveProfitAnalysisMutation({
        bidId,
        profitAnalysisData,
      });

      // Notify user of successful save
      toast.success("Projected profit saved");

      // Call the onSave callback if provided
      if (onSave) {
        onSave(profitAnalysisData);
      }
    } catch (error) {
      console.error("Error saving profit analysis:", error);
      toast.error("Failed to save profit analysis");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-white p-6">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold">Profit Analyzer Tool</h2>
          <p className="text-gray-500">Analyze profitability and break-even points for your bid</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={isSaving || isLoading || !initialized}
          className="gap-2 self-start sm:self-auto"
        >
          {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save Projected Profit
        </Button>
      </div>

      {isLoading || !initialized ? (
        <div className="flex h-64 flex-col items-center justify-center">
          <Loader2 className="mb-4 h-8 w-8 animate-spin text-blue-500" />
          <span className="text-lg">Loading profit analysis data...</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_350px]">
          <div className="space-y-6">
            <Card className="p-6">
              <h2 className="mb-4 text-lg font-semibold">Revenue & Costs</h2>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="revenue">Monthly Revenue ($)</Label>
                    <Input
                      id="revenue"
                      type="number"
                      value={revenue.toFixed(2)}
                      readOnly
                      className="cursor-not-allowed bg-gray-50"
                      title="Revenue is derived from bid pricing"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="labor-cost">Monthly Labor Cost ($)</Label>
                    <Input
                      id="labor-cost"
                      type="number"
                      value={laborCost.toFixed(2)}
                      readOnly
                      className="cursor-not-allowed bg-gray-50"
                      title="Labor cost is derived from Cost Tool"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="material-cost">Monthly Supply Cost ($)</Label>
                    <Input
                      id="material-cost"
                      type="number"
                      value={materialCost.toFixed(2)}
                      readOnly
                      className="cursor-not-allowed bg-gray-50"
                      title="Supply cost is derived from Cost Tool"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="overhead-cost">Monthly Operational Cost ($)</Label>
                    <Input
                      id="overhead-cost"
                      type="number"
                      value={overheadCost.toFixed(2)}
                      readOnly
                      className="cursor-not-allowed bg-gray-50"
                      title="Operational cost is derived from Cost Tool"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="tax-rate">Tax Rate (%)</Label>
                    <Input
                      id="tax-rate"
                      type="number"
                      value={taxRate.toFixed(1)}
                      onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
                    />
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="mb-4 text-lg font-semibold">Cost Breakdown</h2>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                <div className="rounded-md bg-blue-50 p-4 text-center">
                  <p className="text-sm text-gray-600">Labor</p>
                  <p className="text-lg font-semibold">{formatCurrency(laborCost)}</p>
                  <p className="text-xs text-gray-500">
                    {((laborCost / totalCost) * 100).toFixed(1)}% of costs
                  </p>
                </div>

                <div className="rounded-md bg-green-50 p-4 text-center">
                  <p className="text-sm text-gray-600">Supplies</p>
                  <p className="text-lg font-semibold">{formatCurrency(materialCost)}</p>
                  <p className="text-xs text-gray-500">
                    {((materialCost / totalCost) * 100).toFixed(1)}% of costs
                  </p>
                </div>

                <div className="rounded-md bg-purple-50 p-4 text-center">
                  <p className="text-sm text-gray-600">Operational</p>
                  <p className="text-lg font-semibold">{formatCurrency(overheadCost)}</p>
                  <p className="text-xs text-gray-500">
                    {((overheadCost / totalCost) * 100).toFixed(1)}% of costs
                  </p>
                </div>
              </div>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="p-6">
              <h2 className="mb-4 text-lg font-semibold">Profit Analysis</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Total Revenue:</span>
                  <span className="text-lg font-semibold">{formatCurrency(revenue)}</span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Total Costs:</span>
                  <span className="text-lg font-semibold">{formatCurrency(totalCost)}</span>
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Gross Profit:</span>
                  <span className="text-lg font-semibold">{formatCurrency(grossProfit)}</span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Gross Profit Margin:</span>
                  <span className="text-lg font-semibold">{grossProfitMargin.toFixed(1)}%</span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Tax Amount:</span>
                  <span className="text-lg font-semibold">{formatCurrency(taxAmount)}</span>
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Net Profit:</span>
                  <span className="text-xl font-bold text-blue-600">
                    {formatCurrency(netProfit)}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Net Profit Margin:</span>
                  <span className="text-xl font-bold text-blue-600">
                    {netProfitMargin.toFixed(1)}%
                  </span>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="mb-4 text-lg font-semibold">Break-Even Analysis</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Break-Even Revenue:</span>
                  <span className="text-lg font-semibold">{formatCurrency(breakEvenRevenue)}</span>
                </div>

                <div className="rounded-md bg-gray-50 p-4">
                  <p className="text-sm text-gray-600">
                    {revenue > breakEvenRevenue ? (
                      <>
                        You are{" "}
                        <span className="font-medium text-green-600">
                          {formatCurrency(revenue - breakEvenRevenue)}
                        </span>{" "}
                        above break-even point.
                      </>
                    ) : (
                      <>
                        You need{" "}
                        <span className="font-medium text-red-600">
                          {formatCurrency(breakEvenRevenue - revenue)}
                        </span>{" "}
                        more in revenue to break even.
                      </>
                    )}
                  </p>
                </div>
              </div>
            </Card>

            <Button
              onClick={handleSave}
              disabled={isSaving || isLoading || !initialized}
              className="w-full gap-2"
              size="lg"
            >
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save Projected Profit
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
